/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

import { cssMap as cssMapCompiled, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import Checkbox from '@atlaskit/checkbox';
import { Field } from '@atlaskit/form';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor, Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';

import { TeamMembershipSettings } from '../../types';
import { messages } from '../i18n/messages';

import { CheckboxLine } from './styled';

// TODO: After ptc-people-teams-migrate-styled-components FF cleanup:
// - Remove wrapper pattern (fg() conditionals) and use css={} directly
// - Remove Styled imports and styled.ts references
const compiledStyles = cssMapCompiled({
	checkboxLine: {
		display: 'flex',
		alignItems: 'center',
	},
	openMembershipDescriptionAnchor: {
		display: 'contents',
	},
	openMembershipDescription: {
		marginInlineStart: token('space.050'),
	},
});

interface TeamTypePickerProps {
	defaultValue?: TeamMembershipSettings;
	onChange: (newValue: TeamMembershipSettings) => void;
}

/**
 * !!It is actually MEMBERSHIP CONTROL, not TEAM TYPE PICKER!!
 */
export const TeamTypePicker = ({
	defaultValue,
	onChange,
}: TeamTypePickerProps): React.JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();
	const [isMembershipOpen, setIsMembershipOpen] = useState(
		defaultValue === TeamMembershipSettings.OPEN,
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const chosenValue = event.currentTarget.checked
				? TeamMembershipSettings.OPEN
				: TeamMembershipSettings.INVITE_ONLY;

			fireEvent('ui.teamTypePicker.clicked', {
				defaultType: defaultValue || null,
				chosenType: chosenValue,
			});

			setIsMembershipOpen(chosenValue === TeamMembershipSettings.OPEN);
			onChange(chosenValue);
		},
		[defaultValue, onChange, fireEvent],
	);

	const label = (
		<Text size="small">
			<FormattedMessage {...messages.teamTypeCheckboxLabel} />
		</Text>
	);

	// TODO: Remove wrapper after ptc-people-teams-migrate-styled-components FF cleanup
	const CheckboxLineWrapper = fg('ptc-people-teams-migrate-styled-components')
		? (props: React.HTMLAttributes<HTMLDivElement>) => (
				<div css={compiledStyles.checkboxLine} {...props} />
			)
		: CheckboxLine;

	if (fg('ptc-unblock-confluence-permissions-changes')) {
		return (
			<Stack space="space.050">
				<Field name="teamType" label={<FormattedMessage {...messages.teamTypePickerLabel} />}>
					{() => (
						<CheckboxLineWrapper>
							<Checkbox
								{...(fg('team_type_picker_a11y_fix') ? { name: 'teamType' } : {})}
								defaultChecked={defaultValue === TeamMembershipSettings.OPEN}
								onChange={handleChange}
								label={label}
							/>
						</CheckboxLineWrapper>
					)}
				</Field>
				{isMembershipOpen && (
					<Box xcss={compiledStyles.openMembershipDescription}>
						<Text color="color.text.subtlest" size="small">
							<FormattedMessage
								{...messages.teamOpenMembershipDisclaimer}
								values={{
									a: (chunks) => (
										<Anchor
											href="https://support.atlassian.com/platform-experiences/docs/start-an-atlassian-team/#Understanding-membership-controls"
											target="_blank"
											rel="noopener noreferrer"
											xcss={compiledStyles.openMembershipDescriptionAnchor}
										>
											{chunks}
										</Anchor>
									),
								}}
							/>
						</Text>
					</Box>
				)}
			</Stack>
		);
	}

	return (
		<Field name="teamType" label={<FormattedMessage {...messages.teamTypePickerLabel} />}>
			{() => (
				<CheckboxLineWrapper>
					<Checkbox
						{...(fg('team_type_picker_a11y_fix') ? { name: 'teamType' } : {})}
						defaultChecked={defaultValue === TeamMembershipSettings.OPEN}
						onChange={handleChange}
						label={label}
					/>
				</CheckboxLineWrapper>
			)}
		</Field>
	);
};

export default TeamTypePicker;
